import React from 'react';
import { round } from 'lodash';
import DisplayDocument from './DisplayDocument';
import StaticStarUI from '../UI/StaticStarUI';

/**
 * Clearing agent summary for the Ongoing Job / Job in Progress screens.
 *
 * Visibility is decided by the API, not here. JobService::getClearingCargoStatus()
 * returns `clearingAgentInfo` already shaped for the viewer: operators/supervisors
 * get contact details, importers get only the reputation fields. This component
 * simply renders whatever is present, so it holds no role logic and cannot leak
 * a field the server chose to withhold.
 *
 * Two layouts:
 *  - default: its own titled block, used on the operator screen where all six
 *    fields show.
 *  - inline: no heading and no wrapper, just the field columns, so the caller
 *    can drop them into an existing .row alongside its own fields. Used on the
 *    importer screen so Job Id, Bid Price, Agent Completed Jobs and Rating all
 *    sit on one line.
 *
 * Grid note: this app loads Bootstrap **3.3.7** from a CDN (public/index.html),
 * so the responsive prefixes are col-xs (<768) / col-sm (>=768) / col-md (>=992).
 * `col-12` and other Bootstrap 4 classes do nothing here, and columns must live
 * inside a .row or the floats do not clear.
 *
 * @param {object}  agent   details.clearingAgentInfo
 * @param {string}  title
 * @param {boolean} inline  render bare columns for the caller's own row
 */
const AgentInfo = ({ agent, title = 'Clearing Agent', inline = false }) => {
  if (!agent) return null;

  // col-xs-12 so each field takes a full line on phones rather than being
  // squeezed to half width; four across from md up, two on tablets.
  const colClass = inline
    ? 'col-xs-12 col-sm-6 col-md-3 agent-info-field'
    : 'col-xs-12 col-sm-6 col-md-4 agent-info-field';

  // agent_average_ratings is an AVG() and comes back null when the agent has no
  // ratings yet; react-star-ratings throws on null, hence the count guard.
  const rating = agent.agent_total_ratings ? round(agent.agent_average_ratings, 1) : 0;

  const fields = (
    <>
      {agent.name && (
        <div className={colClass}>
          <DisplayDocument label="Name" value={agent.name} type="text" textColClassName="document" />
        </div>
      )}

      {agent.user_id && (
        <div className={colClass}>
          <DisplayDocument
            label="Agent Id"
            value={agent.user_id}
            type="text"
            textColClassName="document"
          />
        </div>
      )}

      {agent.mobile_number && (
        <div className={colClass}>
          <DisplayDocument
            label="Phone Number"
            value={agent.mobile_number}
            type="text"
            textColClassName="document"
          />
        </div>
      )}

      {agent.email && (
        <div className={colClass}>
          <DisplayDocument
            label="Email Address"
            value={agent.email}
            type="text"
            textColClassName="document"
          />
        </div>
      )}

      <div className={colClass}>
        <DisplayDocument
          label="Agent Completed Jobs"
          value={agent.agent_total_jobs || 0}
          type="text"
          textColClassName="document"
        />
      </div>

      {/* Mirrors DisplayDocument's markup (.document > p > strong, then the
          value) so this field matches the size and spacing of Job Id, Bid Price
          and Agent Completed Jobs sitting on the same line. Stars are sized to
          the 16px body text rather than the component's 25px default. */}
      <div className={colClass}>
        <div className="document">
          <p>
            <strong>Agent Rating:</strong>
          </p>
          <span className="agent-rating-value">
            <StaticStarUI rating={rating} starDimension="16px" />
            <span className="agent-rating-summary">
              {agent.agent_total_ratings
                ? `${rating} (${agent.agent_total_ratings} review${
                    agent.agent_total_ratings === 1 ? '' : 's'
                  })`
                : 'No ratings yet'}
            </span>
          </span>
        </div>
      </div>
    </>
  );

  if (inline) return fields;

  return (
    <div className="col-xs-12 agent-info-block">
      <h5 className="mb-2">{title}</h5>
      <div className="row">{fields}</div>
    </div>
  );
};

export default AgentInfo;
